import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import ModeEditIcon from '@mui/icons-material/ModeEdit';

export const PROFILE_CARD_SX = {
  backgroundColor: 'primaryTheme.shade03',
  borderRadius: 5,
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  mt: 2,
  px: { mobile: 2, tablet: 3, laptop: 3 },
  pt: 2,
  pb: { mobile: 2, tablet: 2.5 },
  boxSizing: 'border-box',
};

export const PROFILE_SECTION_TITLE_SX = {
  fontSize: 14,
  fontWeight: 600,
  color: '#92a9a9',
  lineHeight: 1.4,
};

export const PROFILE_SECTION_HINT_SX = {
  pt: 0.75,
  fontSize: 12,
  lineHeight: 1.55,
  color: '#92a9a9',
};

const EDIT_ICON_BUTTON_SX = {
  flexShrink: 0,
  color: '#b8c9c9',
  border: '1px solid rgba(146, 169, 169, 0.35)',
  borderRadius: 1.5,
  width: 36,
  height: 36,
  '&:hover': {
    color: 'common.white',
    borderColor: 'rgba(146, 169, 169, 0.65)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
};

export function ProfileCard({
  id,
  title,
  titleContent,
  hint,
  onEdit,
  editLabel = 'Edit',
  editing = false,
  showEdit = true,
  actions = null,
  children,
  sx,
}) {
  const heading = titleContent ?? title;

  return (
    <Box id={id} sx={{ ...PROFILE_CARD_SX, ...sx }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1.5,
          minHeight: 36,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {heading && (
            <Typography sx={PROFILE_SECTION_TITLE_SX} component="div">
              {heading}
            </Typography>
          )}
          {hint && (
            <Typography sx={PROFILE_SECTION_HINT_SX} component="div">
              {hint}
            </Typography>
          )}
        </Box>
        {showEdit && !editing && onEdit && (
          <IconButton
            type="button"
            onClick={onEdit}
            aria-label={editLabel}
            size="small"
            sx={EDIT_ICON_BUTTON_SX}
          >
            <ModeEditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {children}
      </Box>

      {actions}
    </Box>
  );
}

export function ProfileCardActions({
  onCancel,
  onSave,
  saveDisabled,
  saveLabel = 'Save Changes',
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        mt: 2,
        pt: 2,
        borderTop: '1px solid rgba(146, 169, 169, 0.2)',
        flexDirection: { mobile: 'column', tablet: 'row' },
        gap: 1,
        alignItems: { mobile: 'stretch', tablet: 'center' },
      }}
    >
      <Button onClick={onCancel}>Cancel</Button>
      <Button
        disabled={saveDisabled}
        onClick={onSave}
        sx={{ backgroundColor: 'primaryTheme.shade04' }}
      >
        {saveLabel}
      </Button>
    </Box>
  );
}

export function ProfileFieldRow({ icon, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 0.75,
        minHeight: 44,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: '#92a9a9',
          mt: 0.15,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>{children}</Box>
    </Box>
  );
}

export function ProfileDefinitionRow({ label, children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { mobile: '1fr', tablet: 'minmax(160px, 200px) 1fr' },
        gap: { mobile: 0.35, tablet: 2 },
        py: 1.25,
        borderBottom: '1px solid rgba(146, 169, 169, 0.15)',
        alignItems: 'start',
        '&:last-child': {
          borderBottom: 'none',
          pb: 0,
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: '#92a9a9',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ fontSize: 16, lineHeight: 1.45, wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );
}

export const PROFILE_VALUE_TEXT_SX = {
  fontSize: 16,
  lineHeight: 1.45,
};
